import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import {
  SubmitHandlerWithDirtyFields,
  buildDirtyObject,
} from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { SetChargePointsLocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/set-charge-points-location.model';
import { removeEmptyFields } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/remove-empty-fields/remove-empty-fields';
import { getSetChargePointsLocationDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/set-charge-points-location.form-default-values';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useCpmsBoSetChargePointsLocationMutation } from '@app/core/service/cpms-bo-charge-point.service';

import {
  UpdateLocationChargePointsContextProvider,
  useLocationContext,
} from '../location.context';
import { LocationChargePointListUpdateFormController } from './location-charge-point-list-update-form/location-charge-point-list-update-form.controller';

interface LocationChargePointListUpdateControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const LocationChargePointListUpdateController = ({
  onCancel,
  onSuccess,
}: LocationChargePointListUpdateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [setChargePointsLocationMutation] =
    useCpmsBoSetChargePointsLocationMutation();
  const location = useLocationContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<
    Pick<SetChargePointsLocationModel, 'chargePointIds' | 'locationId'>
  > = (errors) => {
    Logger.error(errors);
    toast.error(
      t('$.location-charge-point-list-update.error', {
        error: t('global.unknown'),
      }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    Pick<SetChargePointsLocationModel, 'chargePointIds' | 'locationId'>
  > = (fieldValues, _, dirtyFields) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    const sanitizedObject = removeEmptyFields(
      buildDirtyObject(dirtyFields, fieldValues),
    );

    if (!sanitizedObject.chargePointIds) {
      setMultiStepFormState(MultiStepFormState.Default);
      return;
    }

    setChargePointsLocationMutation({
      locationId: fieldValues.locationId,
      chargePointIds: sanitizedObject.chargePointIds,
    })
      .unwrap()
      .then(() => {
        toast.success(t('$.location-charge-point-list-update.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(
          t('$.location-charge-point-list-update.error', {
            error: errorMessage,
          }),
        );
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getSetChargePointsLocationDefaultValues(location);

  return (
    <UpdateLocationChargePointsContextProvider data={data}>
      <LocationChargePointListUpdateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </UpdateLocationChargePointsContextProvider>
  );
};
